/* Atelier Studio — first-party session recorder, replay, analysis and live theme overrides.
   Pairs with the Vitals app embed already installed on this shop.
   Privacy: pointer, scroll, clicks and section dwell only. Never records the value of any
   input, textarea or select, and never reads cookies or customer data. */
(function () {
  'use strict';

  var PIN_HASH = 2110838513;
  var KEY_REC = 'atelier.rec.v1';
  var KEY_EVOLVE = 'atelier.evolve.v1';
  var KEY_OPTOUT = 'atelier.rec.optout';
  var AUTH_KEY = 'atelier.studio.ok';
  var MAX_EVENTS = 1500;
  var MAX_SESSIONS = 25;

  var E_MOVE = 0, E_CLICK = 1, E_SCROLL = 2, E_SECTION = 3, E_RAGE = 4, E_CART = 5, E_END = 6;

  /* ---------------------------------------------------------------- storage */

  function read(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function hash(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) h = (h * 33 + str.charCodeAt(i)) >>> 0;
    return h;
  }

  /* ------------------------------------------------------------ dom helpers */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>]/g, function (c) {
      return c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;';
    });
  }

  /* Short, stable-ish selector for a clicked element. Four levels max. */
  function selectorFor(node) {
    var parts = [];
    var n = node;
    var depth = 0;
    while (n && n.nodeType === 1 && n !== document.body && depth < 4) {
      var part = n.tagName.toLowerCase();
      if (n.id) {
        parts.unshift('#' + n.id);
        break;
      }
      var cls = (n.className && typeof n.className === 'string')
        ? n.className.trim().split(/\s+/).filter(function (c) { return c && c.indexOf('is-') !== 0; })[0]
        : null;
      if (cls) part += '.' + cls;
      parts.unshift(part);
      n = n.parentNode;
      depth++;
    }
    return parts.join(' > ');
  }

  /* Human label for a clicked element, with input values deliberately withheld. */
  function labelFor(node) {
    var tag = node.tagName ? node.tagName.toLowerCase() : '';
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return '[form field]';
    var t = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (!t) t = node.getAttribute('aria-label') || node.getAttribute('title') || '';
    return t.slice(0, 48);
  }

  function sectionLabel(id) {
    var host = document.getElementById('shopify-section-' + id);
    if (host) {
      var h = host.querySelector('h1, h2, .gf-eyebrow');
      if (h) {
        var t = h.textContent.replace(/\s+/g, ' ').trim();
        if (t) return t.slice(0, 40);
      }
    }
    // Template-scoped ids look like "template--23720989884693__ticker". Show the readable tail.
    var tail = id.split('__').pop().replace(/[-_]+/g, ' ').trim();
    return tail.charAt(0).toUpperCase() + tail.slice(1);
  }

  /* ------------------------------------------------------- the vitals bridge */

  var Vitals = {
    state: function () {
      var ld = window.vtlsLiquidData || null;
      var modules = ld && ld.moduleSettings ? Object.keys(ld.moduleSettings) : [];
      return {
        present: !!(window.VITALS || ld),
        bootstrapped: !!(window.VITALS && window.VITALS.bootstrapped),
        buildId: ld ? ld.buildId : null,
        modules: modules,
        moduleCount: modules.length,
        host: ld && ld.apiHosts ? ld.apiHosts[1] : null
      };
    },

    /* Give Vitals (and any other app that detects Dawn) the anchors it expects on our
       custom cards, without touching Atelier styling. */
    decorate: function () {
      var cards = document.querySelectorAll('.gf-card[data-product-id]');
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        if (!c.classList.contains('card-wrapper')) c.classList.add('card-wrapper');
        var body = c.querySelector('.gf-card__body');
        if (body && !body.querySelector('[data-vitals-reviews-anchor]')) {
          var anchor = el('div', 'gf-vtl-stars');
          anchor.setAttribute('data-vitals-reviews-anchor', '');
          anchor.setAttribute('data-product-id', c.getAttribute('data-product-id'));
          body.appendChild(anchor);
        }
      }
      return cards.length;
    },

    /* Ask apps to re-scan after we change the DOM. Uses public events only — never pokes
       Vitals internals, which would risk double-mounting its modules. */
    refresh: function (node) {
      try {
        document.dispatchEvent(new CustomEvent('vitals:refresh'));
        var target = node || document;
        target.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      } catch (e) { /* older browsers */ }
    },

    /* Watch every add-to-cart on the page, wherever it comes from — the theme, a Vitals
       upsell, or the cart page — by listening on the cart endpoints themselves. */
    watchCart: function () {
      var fired = function (body) {
        var title = '';
        try {
          var d = typeof body === 'string' ? JSON.parse(body) : body;
          var item = (d && d.items && d.items[0]) || d;
          title = (item && (item.product_title || item.title)) || '';
        } catch (e) { /* not json */ }
        try {
          document.dispatchEvent(new CustomEvent('atelier:cart-add', { detail: { title: title } }));
        } catch (e) { /* older browsers */ }
      };

      if (window.fetch && !window.fetch.__atelier) {
        var native = window.fetch;
        var wrapped = function (input, init) {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          return native.apply(this, arguments).then(function (res) {
            if (url.indexOf('/cart/add') !== -1 && res.ok) {
              res.clone().text().then(fired).catch(function () { fired(null); });
            }
            return res;
          });
        };
        wrapped.__atelier = true;
        window.fetch = wrapped;
      }

      var XHR = window.XMLHttpRequest;
      if (XHR && !XHR.prototype.__atelierOpen) {
        var open = XHR.prototype.open;
        XHR.prototype.open = function (method, url) {
          this.__atelierUrl = url;
          return open.apply(this, arguments);
        };
        XHR.prototype.__atelierOpen = true;
        var send = XHR.prototype.send;
        XHR.prototype.send = function () {
          var xhr = this;
          xhr.addEventListener('load', function () {
            if (xhr.__atelierUrl && String(xhr.__atelierUrl).indexOf('/cart/add') !== -1 &&
                xhr.status >= 200 && xhr.status < 300) {
              fired(xhr.responseText);
            }
          });
          return send.apply(this, arguments);
        };
      }
    },

    /* GTM is installed on this shop (GTM-MXC3ZLKX) — mirror our events into dataLayer. */
    push: function (name, payload) {
      try {
        window.dataLayer = window.dataLayer || [];
        var obj = { event: 'atelier.' + name };
        for (var k in payload) if (payload.hasOwnProperty(k)) obj[k] = payload[k];
        window.dataLayer.push(obj);
      } catch (e) { /* no-op */ }
    }
  };

  /* ----------------------------------------------------------------- recorder */

  var Rec = {
    session: null,
    lastMove: 0,
    lastScroll: 0,
    lastPos: { x: 0, y: 0 },
    clickBuf: [],
    seenSections: {},
    maxDepth: 0,
    started: false,

    enabled: function () {
      if (window.Shopify && window.Shopify.designMode) return false;
      try { if (localStorage.getItem(KEY_OPTOUT) === '1') return false; } catch (e) {}
      return true;
    },

    start: function () {
      if (this.started || !this.enabled()) return;
      this.started = true;
      var self = this;
      this.session = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        t0: Date.now(),
        path: location.pathname + location.search,
        w: window.innerWidth,
        h: window.innerHeight,
        dpr: window.devicePixelRatio || 1,
        ref: document.referrer ? document.referrer.slice(0, 120) : '',
        mob: window.innerWidth < 750,
        ev: []
      };

      document.addEventListener('pointermove', function (e) { self.onMove(e); }, { passive: true });
      document.addEventListener('click', function (e) { self.onClick(e); }, true);
      window.addEventListener('scroll', function () { self.onScroll(); }, { passive: true });
      document.addEventListener('atelier:cart-add', function (e) {
        self.push([self.dt(), E_CART, 0, 0, (e.detail && e.detail.title) || 'item']);
      });
      this.observeSections();

      window.addEventListener('pagehide', function () { self.end(); });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') self.end();
      });
    },

    dt: function () { return Date.now() - this.session.t0; },

    push: function (tuple) {
      if (!this.session) return;
      if (this.session.ev.length >= MAX_EVENTS) return;
      this.session.ev.push(tuple);
    },

    onMove: function (e) {
      var now = Date.now();
      if (now - this.lastMove < 110) return;
      var x = e.pageX, y = e.pageY;
      if (Math.abs(x - this.lastPos.x) < 6 && Math.abs(y - this.lastPos.y) < 6) return;
      this.lastMove = now;
      this.lastPos = { x: x, y: y };
      this.push([this.dt(), E_MOVE, Math.round(x), Math.round(y)]);
    },

    onClick: function (e) {
      var node = e.target;
      if (node && node.closest && node.closest('.ast')) return;
      var sel = selectorFor(node);
      this.push([this.dt(), E_CLICK, Math.round(e.pageX), Math.round(e.pageY), sel, labelFor(node)]);

      var now = Date.now();
      this.clickBuf.push({ t: now, x: e.clientX, y: e.clientY, sel: sel });
      this.clickBuf = this.clickBuf.filter(function (c) { return now - c.t < 700; });
      if (this.clickBuf.length >= 3) {
        var first = this.clickBuf[0];
        var tight = this.clickBuf.every(function (c) {
          return Math.abs(c.x - first.x) < 30 && Math.abs(c.y - first.y) < 30;
        });
        if (tight) {
          this.push([this.dt(), E_RAGE, Math.round(e.pageX), Math.round(e.pageY), sel]);
          this.clickBuf = [];
        }
      }
    },

    onScroll: function () {
      var now = Date.now();
      if (now - this.lastScroll < 190) return;
      this.lastScroll = now;
      var y = window.scrollY || window.pageYOffset || 0;
      var docH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      var depth = docH > 0 ? Math.min(100, Math.round(((y + window.innerHeight) / docH) * 100)) : 0;
      if (depth > this.maxDepth) this.maxDepth = depth;
      this.push([this.dt(), E_SCROLL, Math.round(y), depth]);
    },

    observeSections: function () {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var id = entry.target.id.replace('shopify-section-', '');
          if (self.seenSections[id]) return;
          self.seenSections[id] = true;
          self.push([self.dt(), E_SECTION, 0, 0, id]);
        });
      }, { threshold: 0.35 });
      var hosts = document.querySelectorAll('main [id^="shopify-section-"]');
      for (var i = 0; i < hosts.length; i++) io.observe(hosts[i]);
    },

    end: function () {
      if (!this.session || this.session.saved) return;
      this.session.saved = true;
      // Take one last scroll sample. Without it the throttle can drop the final position,
      // leaving replay scrolled somewhere the visitor had already left.
      this.lastScroll = 0;
      this.onScroll();
      this.push([this.dt(), E_END, 0, 0, this.maxDepth]);
      this.session.dur = this.dt();
      this.session.depth = this.maxDepth;
      this.session.sections = Object.keys(this.seenSections);

      var all = read(KEY_REC, []);
      all.push(this.session);
      while (all.length > MAX_SESSIONS) all.shift();
      // Trim oldest until it fits the quota rather than silently losing the whole log.
      while (all.length && !write(KEY_REC, all)) all.shift();
    }
  };

  /* ----------------------------------------------------------------- analysis */

  var Analyse = {
    run: function () {
      var sessions = read(KEY_REC, []);
      var out = { sessions: sessions.length, findings: [], metrics: null };
      if (!sessions.length) return out;

      var durations = [], depths = [], mobile = 0, atc = 0, bounced = 0;
      var sectionViews = {}, sectionExits = {}, clicks = {}, rages = {};

      sessions.forEach(function (s) {
        durations.push(s.dur || 0);
        depths.push(s.depth || 0);
        if (s.mob) mobile++;
        if ((s.sections || []).length <= 1) bounced++;
        (s.sections || []).forEach(function (id) {
          sectionViews[id] = (sectionViews[id] || 0) + 1;
        });
        var lastSection = (s.sections || [])[(s.sections || []).length - 1];
        if (lastSection) sectionExits[lastSection] = (sectionExits[lastSection] || 0) + 1;

        (s.ev || []).forEach(function (e) {
          if (e[1] === E_CLICK) {
            var k = e[4] + ' ' + (e[5] || '');
            clicks[k] = (clicks[k] || 0) + 1;
          } else if (e[1] === E_RAGE) {
            rages[e[4]] = (rages[e[4]] || 0) + 1;
          } else if (e[1] === E_CART) {
            atc++;
          }
        });
      });

      function median(arr) {
        if (!arr.length) return 0;
        var a = arr.slice().sort(function (x, y) { return x - y; });
        return a[Math.floor(a.length / 2)];
      }

      var metrics = {
        sessions: sessions.length,
        medianDurationSec: Math.round(median(durations) / 1000),
        medianDepth: median(depths),
        bounceRate: Math.round((bounced / sessions.length) * 100),
        mobileShare: Math.round((mobile / sessions.length) * 100),
        addToCarts: atc,
        atcRate: Math.round((atc / sessions.length) * 100)
      };
      out.metrics = metrics;

      var F = out.findings;

      var exitTop = Object.keys(sectionExits).sort(function (a, b) {
        return sectionExits[b] - sectionExits[a];
      })[0];
      if (exitTop && sessions.length >= 3) {
        var share = Math.round((sectionExits[exitTop] / sessions.length) * 100);
        if (share >= 30) {
          F.push({
            sev: share >= 55 ? 'high' : 'med',
            title: share + '% of sessions end at "' + sectionLabel(exitTop) + '"',
            why: 'This is the last section seen before the visitor leaves. Either it is the natural end of the page, or it stops the scroll.',
            fix: 'Move a stronger product block above it, or shorten it.',
            action: { kind: 'promote', section: exitTop }
          });
        }
      }

      if (metrics.medianDepth && metrics.medianDepth < 35 && sessions.length >= 3) {
        F.push({
          sev: 'high',
          title: 'Median scroll depth is only ' + metrics.medianDepth + '%',
          why: 'Half of visitors never reach the middle of the page, so everything below it is unpaid-for work.',
          fix: 'Pull the best-selling grid up directly under the hero.',
          action: null
        });
      }

      Object.keys(sectionViews).forEach(function (id) {
        if (sectionViews[id] < 3) return;
        var clicked = sessions.some(function (s) {
          return (s.ev || []).some(function (e) {
            return e[1] === E_CLICK && e[4] && e[4].indexOf(id) !== -1;
          });
        });
        var host = document.getElementById('shopify-section-' + id);
        var hasLinks = host && host.querySelectorAll('a, button').length > 0;
        if (!clicked && hasLinks) {
          F.push({
            sev: 'med',
            title: '"' + sectionLabel(id) + '" was viewed ' + sectionViews[id] + 'x with no clicks',
            why: 'The section holds links but earns no interaction — it reads as decoration.',
            fix: 'Give it one clear call to action, or hide it.',
            action: { kind: 'hide', section: id }
          });
        }
      });

      Object.keys(rages).forEach(function (sel) {
        if (rages[sel] < 2) return;
        F.push({
          sev: 'high',
          title: 'Rage clicks on ' + sel,
          why: 'Three or more fast clicks in the same spot, ' + rages[sel] + ' times. The visitor expected something to happen and it did not.',
          fix: 'Make this element actually clickable, or stop it from looking clickable.',
          action: null
        });
      });

      if (sessions.length >= 5 && atc === 0) {
        F.push({
          sev: 'high',
          title: 'No add-to-cart across ' + sessions.length + ' recorded sessions',
          why: 'Visitors browse but never commit. Usually price clarity, trust signals, or a buried buy button.',
          fix: 'Put quick-add on the card and a trust row under the hero.',
          action: null
        });
      }

      var mobDepths = sessions.filter(function (s) { return s.mob; }).map(function (s) { return s.depth || 0; });
      var deskDepths = sessions.filter(function (s) { return !s.mob; }).map(function (s) { return s.depth || 0; });
      if (mobDepths.length >= 2 && deskDepths.length >= 2) {
        var md = median(mobDepths), dd = median(deskDepths);
        if (dd > 0 && md < dd * 0.6) {
          F.push({
            sev: 'med',
            title: 'Mobile reaches ' + md + '% depth vs ' + dd + '% on desktop',
            why: 'The mobile page is costing more scroll for the same content.',
            fix: 'Cut section padding and image height on small screens.',
            action: null
          });
        }
      }

      var topClick = Object.keys(clicks).sort(function (a, b) { return clicks[b] - clicks[a]; })[0];
      if (topClick && clicks[topClick] >= 3) {
        var label = topClick.split(' ')[1] || topClick.split(' ')[0];
        F.push({
          sev: 'low',
          title: 'Most-clicked element: "' + label + '" (' + clicks[topClick] + ' clicks)',
          why: 'This is what the page is actually good at.',
          fix: 'Repeat this pattern higher on the page.',
          action: null
        });
      }

      var rank = { high: 0, med: 1, low: 2 };
      F.sort(function (a, b) { return rank[a.sev] - rank[b.sev]; });
      return out;
    }
  };

  /* ------------------------------------------------------------------ evolve */

  var Evolve = {
    get: function () {
      return read(KEY_EVOLVE, { hide: [], order: [], copy: {} });
    },
    set: function (cfg) {
      write(KEY_EVOLVE, cfg);
    },
    apply: function () {
      var cfg = this.get();
      var css = '';
      (cfg.hide || []).forEach(function (id) {
        css += '#shopify-section-' + id + '{display:none!important}';
      });
      if (cfg.order && cfg.order.length) {
        css += 'main.content-for-layout{display:flex;flex-direction:column}';
        cfg.order.forEach(function (id, i) {
          css += '#shopify-section-' + id + '{order:' + i + '}';
        });
      }
      var tag = document.getElementById('atelier-evolve-css');
      if (!tag) {
        tag = el('style');
        tag.id = 'atelier-evolve-css';
        document.head.appendChild(tag);
      }
      tag.textContent = css;

      Object.keys(cfg.copy || {}).forEach(function (sel) {
        try {
          var node = document.querySelector(sel);
          if (node) node.textContent = cfg.copy[sel];
        } catch (e) { /* bad selector */ }
      });
    },
    reset: function () {
      this.set({ hide: [], order: [], copy: {} });
      this.apply();
    },
    brief: function () {
      var cfg = this.get();
      var a = Analyse.run();
      return JSON.stringify({
        shop: location.host,
        generated: new Date().toISOString(),
        theme: 'Atelier — Vitals Studio',
        vitals: Vitals.state(),
        metrics: a.metrics,
        findings: a.findings.map(function (f) {
          return { sev: f.sev, title: f.title, fix: f.fix };
        }),
        liveOverrides: cfg
      }, null, 2);
    }
  };

  /* ------------------------------------------------------------------ replay */

  /* Dawn sets html{scroll-behavior:smooth}. Replay places the cursor against the scroll
     offset on the same frame, so an animated scroll would leave the two out of sync and
     park the cursor off-screen. 'instant' overrides the CSS and settles synchronously. */
  function scrollNow(top) {
    var y = Math.max(0, Math.round(top));
    try {
      window.scrollTo({ top: y, behavior: 'instant' });
    } catch (e) {
      // Older engines reject the 'instant' keyword; suppress smooth scrolling instead.
      var prev = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, y);
      document.documentElement.style.scrollBehavior = prev;
    }
  }

  var Replay = {
    layer: null,
    timer: null,

    play: function (session) {
      this.stop();
      var self = this;
      var scale = session.w ? (window.innerWidth / session.w) : 1;

      var layer = el('div', 'ast-replay');
      layer.innerHTML =
        '<div class="ast-replay__cursor"></div>' +
        '<div class="ast-replay__bar">' +
        '<button class="ast-replay__btn" data-act="toggle">Pause</button>' +
        '<span class="ast-replay__meta">' + esc(session.path) + ' &middot; recorded ' +
        session.w + '&times;' + session.h + (Math.abs(scale - 1) > 0.15 ? ' &middot; rescaled to fit this window' : '') +
        '</span>' +
        '<span class="ast-replay__time">0.0s</span>' +
        '<button class="ast-replay__btn" data-act="speed">1&times;</button>' +
        '<button class="ast-replay__btn ast-replay__btn--x" data-act="exit">Exit replay</button>' +
        '</div>';
      document.body.appendChild(layer);
      this.layer = layer;

      var cursor = layer.querySelector('.ast-replay__cursor');
      var timeEl = layer.querySelector('.ast-replay__time');
      var events = (session.ev || []).slice();
      var i = 0;
      var speed = 1;
      var paused = false;
      var startWall = Date.now();
      var elapsed = 0;

      layer.addEventListener('click', function (e) {
        var act = e.target.getAttribute && e.target.getAttribute('data-act');
        if (!act) return;
        if (act === 'exit') { self.stop(); }
        else if (act === 'toggle') {
          paused = !paused;
          e.target.textContent = paused ? 'Play' : 'Pause';
          if (!paused) startWall = Date.now() - elapsed / speed;
        } else if (act === 'speed') {
          speed = speed === 1 ? 2 : speed === 2 ? 4 : 1;
          e.target.textContent = speed + '×';
          startWall = Date.now() - elapsed / speed;
        }
      });

      function pulse(x, y, rage) {
        var p = el('div', 'ast-replay__pulse' + (rage ? ' is-rage' : ''));
        p.style.left = x + 'px';
        p.style.top = y + 'px';
        layer.appendChild(p);
        setTimeout(function () { if (p.parentNode) p.parentNode.removeChild(p); }, 900);
      }

      this.timer = setInterval(function () {
        if (paused) return;
        elapsed = (Date.now() - startWall) * speed;
        timeEl.textContent = (elapsed / 1000).toFixed(1) + 's';

        while (i < events.length && events[i][0] <= elapsed) {
          var ev = events[i];
          var type = ev[1];
          if (type === E_MOVE || type === E_CLICK || type === E_RAGE) {
            var pageY = ev[3];
            // Pointer is sampled more often than scroll, so a move can refer to a position
            // the replayed scroll has not reached. Bring it into view rather than parking
            // the cursor off-screen.
            if (pageY < window.scrollY || pageY > window.scrollY + window.innerHeight) {
              scrollNow(pageY - window.innerHeight / 2);
            }
            var px = ev[2] * scale - (window.scrollX || 0);
            var py = pageY - (window.scrollY || 0);
            cursor.style.transform = 'translate(' + px + 'px,' + py + 'px)';
            if (type === E_CLICK) pulse(px, py, false);
            if (type === E_RAGE) pulse(px, py, true);
          } else if (type === E_SCROLL) {
            scrollNow(ev[2]);
          }
          i++;
        }

        if (i >= events.length) {
          timeEl.textContent = (elapsed / 1000).toFixed(1) + 's · end';
          clearInterval(self.timer);
          self.timer = null;
        }
      }, 33);
    },

    stop: function () {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      if (this.layer && this.layer.parentNode) this.layer.parentNode.removeChild(this.layer);
      this.layer = null;
    }
  };

  /* ---------------------------------------------------------------------- ui */

  var UI = {
    root: null,
    tab: 'vitals',

    authed: function () {
      try { return sessionStorage.getItem(AUTH_KEY) === '1'; } catch (e) { return false; }
    },

    open: function () {
      if (!this.authed()) return this.gate();
      this.panel();
    },

    gate: function () {
      var self = this;
      var wrap = el('div', 'ast ast-gate');
      wrap.innerHTML =
        '<div class="ast-gate__box">' +
        '<p class="ast-eyebrow">Atelier</p>' +
        '<h2 class="ast-gate__h">Studio access</h2>' +
        '<p class="ast-gate__p">Enter the studio code to open session replays, analysis and live theme overrides.</p>' +
        '<form class="ast-gate__form"><input class="ast-gate__in" type="password" inputmode="numeric" ' +
        'autocomplete="off" placeholder="Studio code" aria-label="Studio code">' +
        '<button class="ast-btn" type="submit">Enter</button></form>' +
        '<p class="ast-gate__err" hidden>That code is not right.</p>' +
        '<button class="ast-gate__close" aria-label="Close">Close</button>' +
        '</div>';
      document.body.appendChild(wrap);
      var input = wrap.querySelector('.ast-gate__in');
      input.focus();

      wrap.querySelector('.ast-gate__form').addEventListener('submit', function (e) {
        e.preventDefault();
        if (hash(input.value) === PIN_HASH) {
          try { sessionStorage.setItem(AUTH_KEY, '1'); } catch (err) {}
          wrap.remove();
          self.panel();
        } else {
          wrap.querySelector('.ast-gate__err').hidden = false;
          input.value = '';
          input.focus();
        }
      });
      wrap.querySelector('.ast-gate__close').addEventListener('click', function () { wrap.remove(); });
      wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
    },

    panel: function () {
      var self = this;
      if (this.root) this.root.remove();
      var root = el('div', 'ast ast-panel');
      root.innerHTML =
        '<div class="ast-panel__inner">' +
        '<header class="ast-head">' +
        '<div><p class="ast-eyebrow">Atelier</p><h2 class="ast-head__h">Studio</h2></div>' +
        '<button class="ast-head__x" aria-label="Close studio">Close</button>' +
        '</header>' +
        '<nav class="ast-tabs">' +
        '<button data-tab="vitals">Vitals</button>' +
        '<button data-tab="replays">Replays</button>' +
        '<button data-tab="analysis">Analysis</button>' +
        '<button data-tab="evolve">Evolve</button>' +
        '</nav>' +
        '<div class="ast-body"></div>' +
        '</div>';
      document.body.appendChild(root);
      this.root = root;

      root.querySelector('.ast-head__x').addEventListener('click', function () { self.close(); });
      root.querySelectorAll('.ast-tabs button').forEach(function (b) {
        b.addEventListener('click', function () {
          self.tab = b.getAttribute('data-tab');
          self.render();
        });
      });
      document.addEventListener('keydown', this._key = function (e) {
        if (e.key === 'Escape') self.close();
      });
      this.render();
    },

    close: function () {
      if (this.root) this.root.remove();
      this.root = null;
      if (this._key) document.removeEventListener('keydown', this._key);
    },

    render: function () {
      if (!this.root) return;
      var self = this;
      var body = this.root.querySelector('.ast-body');
      this.root.querySelectorAll('.ast-tabs button').forEach(function (b) {
        b.classList.toggle('is-on', b.getAttribute('data-tab') === self.tab);
      });
      body.innerHTML = '';
      if (this.tab === 'vitals') this.renderVitals(body);
      else if (this.tab === 'replays') this.renderReplays(body);
      else if (this.tab === 'analysis') this.renderAnalysis(body);
      else this.renderEvolve(body);
    },

    renderVitals: function (body) {
      var v = Vitals.state();
      var cards = document.querySelectorAll('.gf-card[data-product-id]').length;
      var optout = false;
      try { optout = localStorage.getItem(KEY_OPTOUT) === '1'; } catch (e) {}

      body.innerHTML =
        '<div class="ast-grid">' +
        this.stat(v.present ? 'Connected' : 'Not found', 'Vitals app embed') +
        this.stat(v.moduleCount, 'Vitals modules live') +
        this.stat(cards, 'Cards bridged to Vitals') +
        this.stat(read(KEY_REC, []).length, 'Sessions recorded here') +
        '</div>' +
        '<div class="ast-note">' +
        '<p><strong>What the bridge does.</strong> Atelier’s custom sections use their own class names, ' +
        'so Vitals’ Dawn-shaped detector could not see the product cards. Every card now also carries ' +
        '<code>card-wrapper</code>, <code>data-product-id</code> and a review anchor — the hooks Vitals reviews, ' +
        'upsells and wishlist look for. Dawn’s padding classes are deliberately not copied, so Atelier’s ' +
        'layout is untouched.</p>' +
        '<p><strong>Recording is first-party.</strong> This panel’s replays are recorded by the theme itself, stored ' +
        'only in this browser, and never sent anywhere. It records pointer, scroll, clicks and section dwell. It never ' +
        'records what you type.</p>' +
        '</div>' +
        '<div class="ast-row">' +
        '<a class="ast-btn ast-btn--ghost" href="/admin/apps/vitals" target="_blank" rel="noopener">Open Vitals admin</a>' +
        '<button class="ast-btn ast-btn--ghost" data-act="optout">' +
        (optout ? 'Resume recording my visits' : 'Stop recording my own visits') + '</button>' +
        '<button class="ast-btn ast-btn--ghost" data-act="rebridge">Re-run bridge</button>' +
        '</div>' +
        (v.buildId ? '<p class="ast-fine">Vitals build ' + esc(v.buildId) + ' · modules ' + esc(v.modules.join(', ')) + '</p>' : '');

      var self = this;
      body.addEventListener('click', function (e) {
        var act = e.target.getAttribute && e.target.getAttribute('data-act');
        if (act === 'optout') {
          try {
            if (localStorage.getItem(KEY_OPTOUT) === '1') localStorage.removeItem(KEY_OPTOUT);
            else localStorage.setItem(KEY_OPTOUT, '1');
          } catch (err) {}
          self.render();
        } else if (act === 'rebridge') {
          var n = Vitals.decorate();
          Vitals.refresh();
          e.target.textContent = 'Bridged ' + n + ' cards';
        }
      });
    },

    renderReplays: function (body) {
      var sessions = read(KEY_REC, []).slice().reverse();
      if (!sessions.length) {
        body.innerHTML = '<div class="ast-empty"><p>No sessions recorded yet.</p>' +
          '<p class="ast-fine">A session is saved when a visitor leaves the page. Browse the storefront in another tab, ' +
          'close it, then come back here.</p></div>';
        return;
      }
      var rows = sessions.map(function (s, i) {
        var when = new Date(s.t0);
        return '<li class="ast-sess">' +
          '<div class="ast-sess__main">' +
          '<p class="ast-sess__path">' + esc(s.path) + '</p>' +
          '<p class="ast-fine">' + when.toLocaleString() + ' · ' +
          Math.round((s.dur || 0) / 1000) + 's · ' + (s.depth || 0) + '% depth · ' +
          (s.mob ? 'mobile' : 'desktop') + ' ' + s.w + '×' + s.h + ' · ' +
          (s.ev || []).length + ' events</p>' +
          '</div>' +
          '<button class="ast-btn" data-play="' + i + '">Play</button>' +
          '</li>';
      }).join('');

      body.innerHTML = '<ul class="ast-sessions">' + rows + '</ul>' +
        '<div class="ast-row"><button class="ast-btn ast-btn--ghost" data-act="clear">Delete all recordings</button></div>' +
        '<p class="ast-fine">Replay scrolls this live page and draws the recorded cursor over it. Coordinates are ' +
        'rescaled when the recording window differs from yours, so a mobile recording played on desktop is an ' +
        'approximation.</p>';

      var self = this;
      body.addEventListener('click', function (e) {
        var idx = e.target.getAttribute && e.target.getAttribute('data-play');
        if (idx != null) {
          self.close();
          Replay.play(sessions[parseInt(idx, 10)]);
          return;
        }
        if (e.target.getAttribute && e.target.getAttribute('data-act') === 'clear') {
          write(KEY_REC, []);
          self.render();
        }
      });
    },

    renderAnalysis: function (body) {
      var a = Analyse.run();
      if (!a.sessions) {
        body.innerHTML = '<div class="ast-empty"><p>Nothing to analyse yet.</p>' +
          '<p class="ast-fine">Analysis runs on recorded sessions. Record a few first.</p></div>';
        return;
      }
      var m = a.metrics;
      var head = '<div class="ast-grid">' +
        this.stat(m.sessions, 'Sessions') +
        this.stat(m.medianDurationSec + 's', 'Median time on page') +
        this.stat(m.medianDepth + '%', 'Median scroll depth') +
        this.stat(m.bounceRate + '%', 'Saw one section only') +
        this.stat(m.mobileShare + '%', 'Mobile') +
        this.stat(m.atcRate + '%', 'Reached add to cart') +
        '</div>';

      var list = a.findings.length
        ? '<ul class="ast-find">' + a.findings.map(function (f, i) {
            return '<li class="ast-find__i is-' + f.sev + '">' +
              '<p class="ast-find__t"><span class="ast-sev">' + f.sev + '</span>' + esc(f.title) + '</p>' +
              '<p class="ast-find__w">' + esc(f.why) + '</p>' +
              '<p class="ast-find__f"><strong>Do:</strong> ' + esc(f.fix) + '</p>' +
              (f.action ? '<button class="ast-btn ast-btn--sm" data-apply="' + i + '">Apply now</button>' : '') +
              '</li>';
          }).join('') + '</ul>'
        : '<div class="ast-empty"><p>No problems found in this sample.</p></div>';

      body.innerHTML = head + list +
        '<p class="ast-fine">This analysis is computed here in the browser from the recorded sessions by a fixed set of ' +
        'rules. It is not a language model and it does not call one. The Evolve tab exports it as a brief you can hand ' +
        'to one.</p>';

      var self = this;
      body.addEventListener('click', function (e) {
        var i = e.target.getAttribute && e.target.getAttribute('data-apply');
        if (i == null) return;
        var action = a.findings[parseInt(i, 10)].action;
        var cfg = Evolve.get();
        if (action.kind === 'hide') {
          if (cfg.hide.indexOf(action.section) === -1) cfg.hide.push(action.section);
        } else if (action.kind === 'promote') {
          var ids = Array.prototype.map.call(
            document.querySelectorAll('main [id^="shopify-section-"]'),
            function (n) { return n.id.replace('shopify-section-', ''); }
          );
          var without = ids.filter(function (x) { return x !== action.section; });
          without.splice(1, 0, action.section);
          cfg.order = without;
        }
        Evolve.set(cfg);
        Evolve.apply();
        e.target.textContent = 'Applied';
        e.target.disabled = true;
      });
    },

    renderEvolve: function (body) {
      var cfg = Evolve.get();
      var ids = Array.prototype.map.call(
        document.querySelectorAll('main [id^="shopify-section-"]'),
        function (n) { return n.id.replace('shopify-section-', ''); }
      );
      var rows = ids.map(function (id) {
        var hidden = cfg.hide.indexOf(id) !== -1;
        return '<li class="ast-sec' + (hidden ? ' is-off' : '') + '" data-id="' + esc(id) + '">' +
          '<span class="ast-sec__n">' + esc(sectionLabel(id)) + '</span>' +
          '<span class="ast-sec__acts">' +
          '<button class="ast-btn ast-btn--sm" data-move="up">Up</button>' +
          '<button class="ast-btn ast-btn--sm" data-move="down">Down</button>' +
          '<button class="ast-btn ast-btn--sm" data-toggle="1">' + (hidden ? 'Show' : 'Hide') + '</button>' +
          '</span></li>';
      }).join('');

      body.innerHTML =
        '<p class="ast-lead">Reorder or hide sections and the storefront updates instantly. These are live overrides ' +
        'stored in this browser — they change what you see, not the theme files.</p>' +
        '<ul class="ast-secs">' + rows + '</ul>' +
        '<div class="ast-row">' +
        '<button class="ast-btn" data-act="brief">Copy evolve brief</button>' +
        '<button class="ast-btn ast-btn--ghost" data-act="reset">Reset all overrides</button>' +
        '</div>' +
        '<textarea class="ast-brief" readonly>' + esc(Evolve.brief()) + '</textarea>' +
        '<p class="ast-fine">To make an override permanent it has to be written into the theme files. Copy the brief ' +
        'above and hand it back — the section order, the hidden sections, the metrics and the findings are all in it.</p>';

      var self = this;
      body.addEventListener('click', function (e) {
        var act = e.target.getAttribute && e.target.getAttribute('data-act');
        if (act === 'reset') { Evolve.reset(); self.render(); return; }
        if (act === 'brief') {
          var ta = body.querySelector('.ast-brief');
          ta.select();
          try { document.execCommand('copy'); e.target.textContent = 'Copied'; } catch (err) {}
          return;
        }
        var li = e.target.closest && e.target.closest('.ast-sec');
        if (!li) return;
        var id = li.getAttribute('data-id');
        var c = Evolve.get();
        if (e.target.getAttribute('data-toggle')) {
          var at = c.hide.indexOf(id);
          if (at === -1) c.hide.push(id); else c.hide.splice(at, 1);
        } else {
          var dir = e.target.getAttribute('data-move');
          if (!dir) return;
          var order = (c.order && c.order.length) ? c.order.slice() : ids.slice();
          var pos = order.indexOf(id);
          if (pos === -1) return;
          var next = dir === 'up' ? pos - 1 : pos + 1;
          if (next < 0 || next >= order.length) return;
          order.splice(next, 0, order.splice(pos, 1)[0]);
          c.order = order;
        }
        Evolve.set(c);
        Evolve.apply();
        self.render();
      });
    },

    stat: function (value, label) {
      return '<div class="ast-stat"><p class="ast-stat__v">' + esc(value) + '</p>' +
        '<p class="ast-stat__l">' + esc(label) + '</p></div>';
    }
  };

  /* ------------------------------------------------------------------- boot */

  function boot() {
    Evolve.apply();
    Vitals.decorate();
    Vitals.watchCart();
    Rec.start();

    var opener = document.querySelector('[data-atelier-studio]');
    if (opener) {
      opener.addEventListener('click', function (e) {
        e.preventDefault();
        UI.open();
      });
    }

    document.addEventListener('atelier:cart-add', function (e) {
      Vitals.push('cart_add', { title: (e.detail && e.detail.title) || '' });
    });

    window.AtelierStudio = {
      open: function () { UI.open(); },
      vitals: function () { return Vitals.state(); },
      analyse: function () { return Analyse.run(); },
      sessions: function () { return read(KEY_REC, []); },
      evolve: Evolve
    };
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();